import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Action } from "@/components/ai-elements/actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { aiAssistantService } from "@/services/aiAssistantService";
import { supabase } from "@/lib/supabase";
import { folderService, FolderTreeNode } from "@/services/folderService";
import { Save, Folder, ChevronRight, ChevronDown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SaveChatMessageButtonProps {
  messageContent: string;
  assistantId: string;
  assistantInstanceId: string;
  messageTitle?: string;
  className?: string;
  messageId?: string;
  onSaved?: (outputId: string) => void;
}

interface FolderNodeProps {
  node: FolderTreeNode;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  expandedFolders: Set<string>;
  onToggleExpanded: (folderId: string) => void;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  node,
  selectedFolderId,
  onSelectFolder,
  expandedFolders,
  onToggleExpanded,
}) => {
  const hasChildren = node.output_count > 0; // This is a placeholder - we'd need to check for actual child folders
  const isExpanded = expandedFolders.has(node.id);
  const isSelected = selectedFolderId === node.id;

  return (
    <div className="w-full">
      <div
        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
          isSelected ? "bg-blue-50 border border-blue-200" : ""
        }`}
        style={{ marginLeft: `${node.depth * 20}px` }}
        onClick={() => onSelectFolder(node.id)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(node.id);
            }}
            className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        )}
        {!hasChildren && <div className="w-5" />} {/* Spacer for alignment */}
        <Folder className="h-4 w-4 text-gray-600 flex-shrink-0" />
        <span className="text-sm truncate flex-1">{node.name}</span>
        {node.output_count > 0 && (
          <span className="text-xs text-gray-500 flex-shrink-0">
            ({node.output_count})
          </span>
        )}
      </div>
    </div>
  );
};

export function SaveChatMessageButton({
  messageContent,
  assistantId,
  assistantInstanceId,
  messageTitle,
  className,
  messageId,
  onSaved,
}: SaveChatMessageButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [outputTitle, setOutputTitle] = useState(
    messageTitle || "Chat Message",
  );
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [showAllFolders, setShowAllFolders] = useState(false);
  const { organization, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch folder tree
  const { data: folderTree, isLoading: isLoadingFolders } = useQuery({
    queryKey: ["folderTree", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const result = await folderService.getFolderTree(organization.id);
      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!organization?.id && isDialogOpen,
  });

  // Check if this message is already saved as an output and get folder info
  const { data: savedOutputInfo } = useQuery({
    queryKey: ["messageOutput", messageId],
    queryFn: async () => {
      if (!messageId) return null;

      // Get the output_id from chat_messages
      const { data: messageData, error: messageError } = await supabase
        .from("chat_messages")
        .select("output_id")
        .eq("id", messageId)
        .single();

      if (messageError || !messageData?.output_id) return null;

      // Get the folder information from the output
      const { data: outputData, error: outputError } = await supabase
        .from("ai_outputs")
        .select("folder_id, folders(id, name)")
        .eq("id", messageData.output_id)
        .single();

      if (outputError) return { outputId: messageData.output_id, folder: null };

      return {
        outputId: messageData.output_id,
        folder: outputData?.folders
          ? {
              id: outputData.folders.id,
              name: outputData.folders.name,
            }
          : null,
      };
    },
    enabled: !!messageId,
  });

  const isSaved = !!savedOutputInfo?.outputId;

  // Create chat message output mutation
  const saveMessageMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id || !user?.id) {
        throw new Error("Organization or user not found");
      }

      const result = await aiAssistantService.createChatMessageOutput({
        assistantId,
        assistantInstanceId,
        organizationId: organization.id,
        userId: user.id,
        messageContent,
        messageTitle: outputTitle.trim() || "Chat Message",
        folderId: selectedFolderId,
        messageId,
      });

      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: (output) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      queryClient.invalidateQueries({ queryKey: ["savedOutputs"] });
      queryClient.invalidateQueries({ queryKey: ["messageOutput", messageId] });

      setIsDialogOpen(false);

      // Notify parent that this message was saved
      onSaved?.(output?.id || "");

      const folderName =
        folderTree?.find((f) => f.id === selectedFolderId)?.name ||
        "Selected Folder";

      toast({
        title: "Message Saved",
        description: (
          <div className="flex flex-col gap-2">
            <span>Saved to {folderName}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigate(`/folders?folder=${selectedFolderId}`);
              }}
            >
              View in Folder
            </Button>
          </div>
        ),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error?.message || "Failed to save message",
        variant: "destructive",
      });
    },
  });

  const handleToggleExpanded = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Show saved indicator if message is already saved
  if (isSaved && savedOutputInfo?.folder) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
        <span className="text-xs text-gray-600">Saved in</span>
        <button
          onClick={() =>
            navigate(`/folders?folder=${savedOutputInfo.folder.id}`)
          }
          className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate max-w-20"
          title={`View in ${savedOutputInfo.folder.name}`}
        >
          {savedOutputInfo.folder.name}
        </button>
      </div>
    );
  }

  // Show generic saved state if no folder info
  if (isSaved) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Check className="h-3 w-3 text-green-600" />
        <span className="text-xs text-gray-600">Saved</span>
      </div>
    );
  }

  return (
    <>
      <Action
        tooltip="Save message to folder"
        onClick={() => {
          setOutputTitle(messageTitle || "Chat Message");
          setSelectedFolderId(null);
          setShowAllFolders(false);
          setIsDialogOpen(true);
        }}
        className={className}
      >
        <Save className="h-4 w-4" />
      </Action>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Message to Folder</DialogTitle>
            <DialogDescription>
              Choose a folder and name for this message output.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Title Input */}
            <div className="space-y-2">
              <Label htmlFor="output-title">Title</Label>
              <Input
                id="output-title"
                value={outputTitle}
                onChange={(e) => setOutputTitle(e.target.value)}
                placeholder="Enter a title for this output"
                className="w-full"
              />
            </div>

            {/* Folder tree */}
            <div className="max-h-60 overflow-y-auto border rounded-md">
              {isLoadingFolders ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Loading folders...
                </div>
              ) : folderTree && folderTree.length > 0 ? (
                <div className="p-2">
                  {/* Show first 4 folders or all if expanded */}
                  {(showAllFolders ? folderTree : folderTree.slice(0, 4)).map(
                    (node) => (
                      <FolderNode
                        key={node.id}
                        node={node}
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={setSelectedFolderId}
                        expandedFolders={expandedFolders}
                        onToggleExpanded={handleToggleExpanded}
                      />
                    ),
                  )}

                  {/* Show "More" button if there are more than 4 folders */}
                  {folderTree.length > 4 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllFolders(!showAllFolders)}
                        className="w-full justify-center text-xs text-gray-600 hover:text-gray-800"
                      >
                        {showAllFolders ? (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Show Less ({folderTree.length - 4} hidden)
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-3 w-3 mr-1" />
                            More ({folderTree.length - 4} more)
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-gray-500">
                  No folders found. You can create folders in the Library.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={saveMessageMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveMessageMutation.mutate()}
              disabled={
                saveMessageMutation.isPending ||
                !outputTitle.trim() ||
                !selectedFolderId
              }
            >
              {saveMessageMutation.isPending ? "Saving..." : "Save Message"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
